/**
 * Clothing size guide table: CM/IN and UK/US/EU switchers.
 * Expects window.TwaSizeGuideConfig.regionLabels (set via Liquid snippet).
 */
(function () {
  'use strict';

  var CM_PER_IN = 2.54;

  var DEFAULT_REGION_LABELS = {
    UK: { XS: 'XS', S: 'S', M: 'M', L: 'L', XL: 'XL', XXL: 'XXL' },
    US: {
      XS: '34(XS)',
      S: '36(S)',
      M: '38(M)',
      L: '40(L)',
      XL: '42(XL)',
      XXL: '44(XXL)',
    },
    EU: {
      XS: '44(XS)',
      S: '46(S)',
      M: '48(M)',
      L: '50(L)',
      XL: '52(XL)',
      XXL: '54(XXL)',
    },
  };

  function uppercaseSizeKeys(map) {
    if (!map || typeof map !== 'object' || Array.isArray(map)) {
      return {};
    }
    var inner = {};
    var sk;
    for (sk in map) {
      if (!Object.prototype.hasOwnProperty.call(map, sk)) {
        continue;
      }
      inner[String(sk).toUpperCase()] = map[sk];
    }
    return inner;
  }

  function normalizeClothingRegionLabels(obj) {
    if (!obj || typeof obj !== 'object') {
      return DEFAULT_REGION_LABELS;
    }
    var out = {};
    var k;
    for (k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) {
        continue;
      }
      var rk = String(k).toUpperCase();
      var rv = obj[k];
      out[rk] =
        rv && typeof rv === 'object' && !Array.isArray(rv)
          ? uppercaseSizeKeys(rv)
          : rv;
    }
    return out;
  }

  function resolveRegionLabels() {
    var raw =
      window.TwaSizeGuideConfig &&
      window.TwaSizeGuideConfig.regionLabels;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch (ignore) {
        raw = null;
      }
    }
    return normalizeClothingRegionLabels(raw);
  }

  var REGION_LABELS = resolveRegionLabels();

  function formatMeasure(cm, useInches) {
    var v = useInches ? cm / CM_PER_IN : cm;
    var rounded = Math.round(v * 10) / 10;
    if (rounded === Math.floor(rounded)) {
      return String(Math.round(rounded));
    }
    return String(rounded);
  }

  function parseMeasurementCm(raw) {
    var t = String(raw || '').trim();
    if (!t) {
      return null;
    }
    var rangeMatch = t.match(/^([\d.,]+)\s*[-–—]\s*([\d.,]+)/);
    if (rangeMatch) {
      var low = parseFloat(rangeMatch[1].replace(',', '.'));
      var high = parseFloat(rangeMatch[2].replace(',', '.'));
      if (Number.isFinite(low) && Number.isFinite(high)) {
        return { low: low, high: high };
      }
      return null;
    }
    var n = parseFloat(t.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  function formatMeasurement(cmOrRange, useInches) {
    if (cmOrRange == null) {
      return '';
    }
    if (
      typeof cmOrRange === 'object' &&
      Number.isFinite(cmOrRange.low) &&
      Number.isFinite(cmOrRange.high)
    ) {
      return (
        formatMeasure(cmOrRange.low, useInches) +
        ' - ' +
        formatMeasure(cmOrRange.high, useInches)
      );
    }
    return formatMeasure(cmOrRange, useInches);
  }

  function sizeKeyFromCell(text) {
    var u = text.trim().toUpperCase();
    if (REGION_LABELS.UK[u]) {
      return u;
    }
    var k;
    for (k in REGION_LABELS.EU) {
      if (
        Object.prototype.hasOwnProperty.call(REGION_LABELS.EU, k) &&
        REGION_LABELS.EU[k] === u
      ) {
        return k;
      }
    }
    return u;
  }

  function normalizeRegionKey(reg) {
    var u = (reg && String(reg).toUpperCase()) || 'UK';
    return REGION_LABELS[u] ? u : 'UK';
  }

  class TwaSizeGuideTable extends HTMLElement {
    constructor() {
      super();
      this._rows = null;
      this._sizeCol = 0;
      this._firstDataRowIndex = 1;
      this._region = 'UK';
      this._unit = 'CM';
      this._tableEl = null;
      this._switcherClickBound = this._onSwitcherClick.bind(this);
    }

    connectedCallback() {
      var self = this;
      queueMicrotask(function () {
        self.init();
      });
    }

    disconnectedCallback() {
      this._unbindControls();
      this._rows = null;
      this._tableEl = null;
      this._switcherRoot = null;
    }

    resolveSwitcherRoot() {
      var pid = this.dataset && this.dataset.productid;
      if (pid) {
        var byPid = this.querySelector('#twa-sizechart-' + pid);
        if (byPid) {
          this._switcherRoot = byPid;
          return;
        }
      }
      var fallback = this.querySelector('.twa-sizechart-switcher');
      this._switcherRoot = fallback || this;
    }

    init() {
      this.resolveSwitcherRoot();
      this.bindFromDomState();
      this.bindControls();

      var wrap = this.querySelector('.twa-sizechart-table');
      if (!wrap) {
        return;
      }

      this._tableEl = wrap.querySelector('table');
      if (!this._tableEl) {
        return;
      }

      if (this.captureModel()) {
        this.render();
      }
    }

    bindFromDomState() {
      var root = this._switcherRoot || this;
      var activeU = root.querySelector(
        '.twa-sizechart-switcher-unit ' +
          'button.twa-sizechart-switcher-unit-btn-active[data-unit]'
      );
      var activeR = root.querySelector(
        '.twa-sizechart-switcher-select ' +
          'button.twa-sizechart-switcher-unit-btn-active[data-region]'
      );
      if (activeU) {
        var au = activeU.getAttribute('data-unit') || activeU.dataset.unit;
        if (au) {
          this._unit = au;
        }
      }
      if (activeR) {
        var ar =
          activeR.getAttribute('data-region') || activeR.dataset.region;
        if (ar) {
          this._region = normalizeRegionKey(ar.trim());
        }
      }
    }

    captureModel() {
      var table = this._tableEl;
      var trList = table.querySelectorAll('tr');
      if (trList.length < 2) {
        return false;
      }

      var headerRowIndex = -1;
      var sizeCol = 0;
      var hi;
      var ci;
      for (hi = 0; hi < trList.length; hi++) {
        var headerCells = trList[hi].querySelectorAll('th, td');
        for (ci = 0; ci < headerCells.length; ci++) {
          if (
            headerCells[ci].textContent.trim().toLowerCase() === 'size'
          ) {
            headerRowIndex = hi;
            sizeCol = ci;
            break;
          }
        }
        if (headerRowIndex >= 0) {
          break;
        }
      }

      if (headerRowIndex < 0) {
        headerRowIndex = 0;
        var headerCells0 = trList[0].querySelectorAll('th, td');
        sizeCol = 0;
        for (ci = 0; ci < headerCells0.length; ci++) {
          if (
            headerCells0[ci].textContent.trim().toLowerCase() === 'size'
          ) {
            sizeCol = ci;
            break;
          }
        }
      }

      this._sizeCol = sizeCol;
      this._firstDataRowIndex = headerRowIndex + 1;
      var rows = [];
      var r;
      for (r = this._firstDataRowIndex; r < trList.length; r++) {
        var cells = trList[r].querySelectorAll('th, td');
        if (cells.length === 0) {
          continue;
        }

        if (!cells[sizeCol]) {
          continue;
        }

        var key = sizeKeyFromCell(cells[sizeCol].textContent);
        var vals = [];
        var c;
        for (c = 0; c < cells.length; c++) {
          if (c === sizeCol) {
            continue;
          }
          var raw = cells[c].textContent.trim();
          vals.push(parseMeasurementCm(raw));
        }
        rows.push({ key: key, valuesCm: vals });
      }

      this._rows = rows;
      return rows.length > 0;
    }

    _unbindControls() {
      this.removeEventListener('click', this._switcherClickBound);
    }

    bindControls() {
      this._unbindControls();
      this.addEventListener('click', this._switcherClickBound);
    }

    _onSwitcherClick(ev) {
      var node = ev.target;
      if (!node || !this.contains(node)) {
        return;
      }

      var regionBtn =
        typeof node.closest === 'function'
          ? node.closest('button[data-region]')
          : null;
      var root = this._switcherRoot || this;
      if (
        regionBtn &&
        this.contains(regionBtn) &&
        root.contains(regionBtn)
      ) {
        var reg =
          regionBtn.getAttribute('data-region') || regionBtn.dataset.region;
        if (!reg) {
          return;
        }
        ev.preventDefault();
        this._region = normalizeRegionKey(reg.trim());
        var sel = root.querySelector('.twa-sizechart-switcher-select');
        if (sel) {
          var all = sel.querySelectorAll('button[data-region]');
          var j;
          for (j = 0; j < all.length; j++) {
            all[j].classList.toggle(
              'twa-sizechart-switcher-unit-btn-active',
              all[j] === regionBtn
            );
          }
          if ('open' in sel) {
            sel.open = false;
          }
        }
        this.render();
        return;
      }

      var unitBtn =
        typeof node.closest === 'function'
          ? node.closest('button[data-unit]')
          : null;
      if (
        !unitBtn ||
        !this.contains(unitBtn) ||
        !root.contains(unitBtn)
      ) {
        return;
      }

      var u = unitBtn.getAttribute('data-unit') || unitBtn.dataset.unit;
      if (!u) {
        return;
      }
      ev.preventDefault();
      this._unit = u;
      var wrap = root.querySelector('.twa-sizechart-switcher-unit');
      if (wrap) {
        var allU = wrap.querySelectorAll('button[data-unit]');
        var k;
        for (k = 0; k < allU.length; k++) {
          allU[k].classList.toggle(
            'twa-sizechart-switcher-unit-btn-active',
            allU[k] === unitBtn
          );
        }
      }
      this.render();
    }

    render() {
      if (!this._rows || !this._tableEl) {
        return;
      }

      var trList = this._tableEl.querySelectorAll('tr');
      var map = REGION_LABELS[this._region] || REGION_LABELS.UK;
      var useIn = this._unit === 'IN';
      var sizeCol = this._sizeCol;
      var firstData = this._firstDataRowIndex;
      var idx;
      for (idx = 0; idx < this._rows.length; idx++) {
        var row = this._rows[idx];
        var tr = trList[firstData + idx];
        if (!tr) {
          break;
        }

        var cells = tr.querySelectorAll('th, td');
        var label = map[row.key];
        if (!label) {
          label = REGION_LABELS.UK[row.key] || row.key;
        }
        if (cells[sizeCol]) {
          cells[sizeCol].textContent = label;
        }

        var vi = 0;
        var ci;
        for (ci = 0; ci < cells.length; ci++) {
          if (ci === sizeCol) {
            continue;
          }
          var cm = row.valuesCm[vi];
          vi++;
          if (cm == null) {
            continue;
          }
          cells[ci].textContent = formatMeasurement(cm, useIn);
        }
      }
    }
  }

  if (!customElements.get('twa-size-guide-table')) {
    customElements.define('twa-size-guide-table', TwaSizeGuideTable);
  }
})();
